/**
 * Design 2026-09-07 §4, revised 23.09.2026 (Denis: the app says „du", and the
 * greeting is personal). Data, not code: strike, change, add without touching
 * the logic in `pickGreeting.ts`.
 *
 * **Numbers only through placeholders.** A fixed sentence that claims a count
 * stood once beside six open cases („Ein Vorgang steht noch in der Planung",
 * 07.09.2026). `{n}` is the count as a word (zwei … zwölf, then digits),
 * `{caseNo}` the case number — both filled in where the figure is known.
 */

/** Said when nothing concrete waits: by weekday first, then by the hour. */
export const GREETINGS = {
  /** Before 7 in the morning. */
  early: [
    "Früh ist eine gute Zeit für Maße — noch ruft keiner an.",
    "Die Werkbank ist schon warm.",
  ],
  /** Monday before noon. */
  monday: [
    "Neue Woche, leere Werkbank. Die erste Zeichnung gibt den Takt vor.",
    "Montag. Erst die Form, dann die Maße.",
  ],
  /** Friday from 14:00. */
  friday: [
    "Was heute noch ins Angebot geht, geht raus. Der Rest wartet bis Montag.",
    "Letzter Rostlauf der Woche.",
  ],
  /** Saturday and Sunday. */
  weekend: [
    "Wochenende, und die Werkbank hat trotzdem auf.",
    "Am Wochenende ist es ruhig — gut zum Nachmessen.",
  ],
  morning: [
    "Zeichnung rein, Stückliste raus.",
    "Die Maschen sind geschnitten, der Kaffee noch nicht.",
    "Erst die Form, dann die Maße.",
  ],
  midday: [
    "Halbzeit. Ein Rostlauf geht noch vor dem Essen.",
    "Restmaß unter 400 mm gibt es nicht — Mittag schon.",
  ],
  afternoon: [
    "Ein Angebot, das heute rausgeht, ist morgen beim Kunden.",
    "Konsolen zählen sich nicht von allein. Doch — hier schon.",
  ],
  /** From 19:00 and through the night. */
  late: [
    "Spät, aber die Schließungsprüfung schläft nie.",
    "Feierabend ist auch ein Arbeitsschritt.",
  ],
} as const;

export type GreetingGroup = keyof typeof GREETINGS;

/**
 * The very first visit — no case yet. Says how to begin, in one sentence.
 * No direction in it (UX gate round 1, #14): „auf die linke Fläche" was false
 * wherever the two areas stack — on a phone there is no left. „Willkommen"
 * above already says what „Schön, dass du da bist" said.
 */
export const FIRST_VISIT = "Lies eine Zeichnung ein — oder zeichne den Umriss selbst, wenn keine da ist.";

/**
 * A short lead before the concrete sentence, when the day of the week has
 * something to say. Only the first matching lead is used.
 */
export const LEADS = {
  monday: "Neue Woche.",
  friday: "Die Woche ist fast durch.",
  weekend: "Wochenende, und du bist hier.",
} as const;

/**
 * A colleague wants to hand me a Vorgang (UX gate round 1, #1). It comes
 * before everything in `WAITING`: a request lapses after seven days, and the
 * answer to it stands right under the greeting. `{from}` is the sender's first
 * name, as the salutation uses mine; the card below carries the full name.
 */
export const HANDOVER = {
  one: "{from} möchte dir „{title}“ übergeben.",
  many: "{n} Übergaben warten auf deine Antwort.",
} as const;

/**
 * What waits, closest to the quote first. `one` names the case, `many` counts.
 * The order of the keys is the priority: the first kind that has a case wins.
 */
export const WAITING = {
  approved: {
    one: "Vorgang {caseNo} ist freigegeben — das Angebot kann raus.",
    many: "{n} Vorgänge sind freigegeben — die Angebote können raus.",
  },
  release: {
    one: "Die Kalkulation zu {caseNo} wartet auf deine Freigabe.",
    many: "{n} Kalkulationen warten auf deine Freigabe.",
  },
  planning: {
    one: "Vorgang {caseNo} steht in der Planung.",
    many: "{n} Vorgänge stehen in der Planung.",
  },
  documents: {
    one: "Zu {caseNo} liegen Unterlagen, ein Stand fehlt noch.",
    many: "{n} Vorgänge haben Unterlagen, aber noch keinen Stand.",
  },
  new: {
    one: "Vorgang {caseNo} ist neu angelegt.",
    many: "{n} Vorgänge sind neu angelegt.",
  },
  sent: {
    one: "Das Angebot zu {caseNo} ist raus — die Antwort steht aus.",
    many: "{n} Angebote sind raus, die Antworten stehen aus.",
  },
} as const;

export type WaitingKind = keyof typeof WAITING;

/**
 * Spruch des Tages — one per calendar day, the same on every reload.
 *
 * Own sentences or old workshop sayings without an author: no quotes with
 * names (rights, and whether the person really said it). Grounded in the
 * trade — measuring, steel, planning — no calendar poster, no exclamation
 * mark. Nothing here states a Fachregel as fact that Chiara has not
 * confirmed.
 */
export const SAYINGS = [
  "Zweimal messen, einmal schneiden.",
  "Ein Maß ohne Quelle ist eine Vermutung.",
  "Die Form verrät ein falsches Maß früher als jede Liste.",
  "Ein sauberer Stoß braucht zwei gerade Kanten.",
  "Was auf der Baustelle klemmt, hat am Schreibtisch angefangen.",
  "Ein Millimeter ist wenig, bis er sich fünfmal wiederholt.",
  "Restmaße plant man vorher, nicht auf der Baustelle.",
  "Stahl verzeiht viel, nur keine falsche Länge.",
  "Gute Planung erkennt man daran, dass auf der Baustelle niemand fragt.",
  "Jede Konsole trägt, was die Zeichnung verspricht.",
  "Eine Rückfrage heute spart eine Nachlieferung morgen.",
  "An der Ecke wird jeder Plan ehrlich.",
  "Wer die Wand kennt, legt den Rost richtig.",
  "Wasser findet jede Fuge, die man vergessen hat.",
  "Verzinkt hält lange. Richtig gemessen hält länger.",
  "Das Schwierige an einem geraden Lauf ist, dass er gerade bleibt.",
  "Ein Plan ist fertig, wenn nichts mehr zu erraten ist.",
  "Eine Skizze sagt, was gemeint ist. Eine Stückliste sagt, was kommt.",
  "Maße aus dem Gedächtnis sind Maße aus zweiter Hand.",
  "Lieber eine offene Frage als eine falsche Antwort.",
  "Die Masche ist klein, der Rost ist lang — beides zählt.",
  "Wer die Schnitte kennt, kennt den Preis.",
  "Fertig ist, was ein anderer ohne Nachfrage bauen kann.",
  "Ein Fallrohr steht immer genau da, wo der Rost hin soll.",
  "Das Haus war zuerst da. Der Rost richtet sich danach.",
  "Genau ist, was sich nachprüfen lässt.",
  "Gerundet wird am Schluss, nicht unterwegs.",
  "Im Detail wohnt die Nachlieferung.",
  "Ein Winkel, der fast rechtwinklig ist, ist nicht rechtwinklig.",
  "Was trägt, wurde vorher gerechnet.",
  "Eine Lücke, die man benennt, ist schon halb geschlossen.",
  "Passung plant man. Hinklopfen ist keine Planung.",
  "Stahl wird nicht kürzer, wenn man ihn freundlich ansieht.",
  "Die beste Konsole ist die, die niemand vermisst.",
  "Ordnung im Plan ist Ordnung auf der Palette.",
  "Ein schiefer Anfang zeigt sich am Ende.",
  "Wenige Teile, gut gewählt, schlagen viele Teile, schnell gewählt.",
  "Präzision ist die Höflichkeit gegenüber dem Monteur.",
  "Jede Wand hat ihr eigenes Maß, auch wenn sie gleich aussieht.",
  "Was einmal geschnitten ist, bleibt geschnitten.",
  "Eine Stückliste ist ein Versprechen in Zeilen.",
  "Gut vorbereitet ist halb montiert.",
  "Die Zahl auf der Skizze ist ein Hinweis. Die Zahl in der Stückliste ist eine Zusage.",
  "Ein Rost, der passt, fällt niemandem auf. Genau so soll es sein.",
  "Wer nachmisst, muss sich nicht entschuldigen.",
] as const;
